import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
	computed,
	effect,
	inject,
	input,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { Subject, filter, takeUntil } from 'rxjs';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { driver } from 'driver.js';
import { WhatsNewComponent } from '../whats-new/whats-new.component';
import { UsersClient } from 'app/core/services/api.service';
import { TaskNotificationStore } from 'app/store/task-notification.store';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from 'app/core/auth/auth.service';
import { PwaAccessService } from 'app/core/pwa/pwa-access.service';

@Component({
	selector: 'user',
	templateUrl: './user.component.html',
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	exportAs: 'user',
	imports: [
		MatButtonModule,
		MatMenuModule,
		MatIconModule,
		NgClass,
		MatDividerModule,
		RouterModule,
		MatTooltipModule,
	],
})
export class UserComponent implements OnInit, OnDestroy {
	/* eslint-disable @typescript-eslint/naming-convention */
	static ngAcceptInputType_showAvatar: BooleanInput;
	/* eslint-enable @typescript-eslint/naming-convention */

	readonly showAvatar = input<boolean>(true);
	user: User;

	private readonly taskNotificationStore = inject(TaskNotificationStore);
	readonly pwaAccess = inject(PwaAccessService);
	private _unsubscribeAll: Subject<any> = new Subject<any>();

	hubStatus = computed(() => this.taskNotificationStore.hubStatus());
	hubStatusTooltip = computed(() => {
		switch (this.hubStatus()) {
			case 'connected':
				return 'Realtime Connected';
			case 'connecting':
				return 'Realtime Connecting';
			default:
				return 'Realtime Disconnected';
		}
	});

	/**
	 * Constructor
	 */
	constructor(
		private _changeDetectorRef: ChangeDetectorRef,
		private _router: Router,
		private _authService: AuthService,
		private _userService: UserService,
		private _dialog: MatDialog,
		private _usersClient: UsersClient,
	) {
		effect(() => {
			const user = this._userService.userSig$();

			this.user = user;

			// Mark for check
			this._changeDetectorRef.markForCheck();
		});
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void {
		this._usersClient
			.hasNewFeature()
			.pipe(
				filter((hasNewFeatures) => hasNewFeatures),
				takeUntil(this._unsubscribeAll),
			)
			.subscribe(() => this.openWhatsNew());
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		// Unsubscribe from all subscriptions
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Sign out
	 */
	signOut(): void {
		this._authService.signOut().subscribe();
	}

	goTo(route: string): void {
		this._router.navigateByUrl(route);
	}

	startTour() {
		const driverObj = driver({
			showProgress: true,
			steps: [
				{
					element: 'user',
					popover: {
						title: 'User Profile',
						description:
							'Change Password, Modify Profile or Sign Out',
					},
				},
				{
					element: '#search',
					popover: {
						title: 'System Wide Search',
						description:
							'Search fo anything in ClaimMosaic in one place',
					},
				},
			],
		});

		driverObj.drive();
	}

	/**
	 * Open Whats New
	 */
	openWhatsNew(): void {
		this._dialog.open(WhatsNewComponent, {
			width: '800px',
		});
	}

	installApp(): void {
		void this.pwaAccess.promptInstall();
	}
}
